import axios from 'axios'

import { getApiBaseUrl } from './apiUrl'

// See apiUrl.ts for the precedence: runtime injected value, then build time, then relative.
const API_URL = getApiBaseUrl()
let sessionExpiryHandled = false
let refreshPromise: Promise<string> | null = null
const REFRESH_BEFORE_EXPIRY_MS = 2 * 60 * 1000

export type SessionEndReason = 'expired' | 'idle' | 'invalid'

/** Clear every local credential and move immediately to a clean sign-in state. */
export function expireClientSession(reason: SessionEndReason = 'expired') {
  localStorage.removeItem('token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user')
  localStorage.removeItem('bms_last_activity_at')

  if (!sessionExpiryHandled) {
    sessionExpiryHandled = true
    window.dispatchEvent(new CustomEvent('auth:session-expired', { detail: { reason } }))
  }

  const currentPath = window.location.pathname
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password']
    .some(path => currentPath.includes(path))
  if (!isAuthPage) {
    window.location.replace(`/login?session=${encodeURIComponent(reason)}`)
  }
}

window.addEventListener('auth:session-established', () => {
  sessionExpiryHandled = false
})

function tokenExpiryMs(token: string): number | null {
  try {
    const encodedPayload = (token.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(encodedPayload.padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=')))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

/** Refresh a near-expiry access token while the idle-session policy is active. */
export async function refreshAccessTokenIfNeeded(force = false): Promise<string | null> {
  const accessToken = localStorage.getItem('token')
  const refreshToken = localStorage.getItem('refresh_token')
  if (!accessToken || !refreshToken) return accessToken

  const expiresAt = tokenExpiryMs(accessToken)
  if (!force && (expiresAt == null || expiresAt - Date.now() > REFRESH_BEFORE_EXPIRY_MS)) {
    return accessToken
  }
  if (refreshPromise) return refreshPromise

  refreshPromise = axios.post(`${API_URL}/api/v1/auth/refresh`, {
    refresh_token: refreshToken,
  }, {
    headers: { 'Content-Type': 'application/json' },
  }).then(response => {
    const nextToken = String(response.data?.access_token || '')
    const nextRefreshToken = String(response.data?.refresh_token || '')
    if (!nextToken) throw new Error('Token refresh returned no access token')
    localStorage.setItem('token', nextToken)
    if (nextRefreshToken) {
      localStorage.setItem('refresh_token', nextRefreshToken)
    }
    sessionExpiryHandled = false
    window.dispatchEvent(new CustomEvent('auth:token-refreshed', {
      detail: { token: nextToken, refreshToken: nextRefreshToken || undefined },
    }))
    return nextToken
  }).finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

export const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const isRefreshRequest = config.url?.includes('/auth/refresh')
    let token = localStorage.getItem('token')
    if (token && !isRefreshRequest) {
      try {
        token = await refreshAccessTokenIfNeeded() || token
      } catch {
        // Let the request use the current token. A 401 will take the forced
        // refresh path below and only then end the session if renewal fails.
      }
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  async (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const detail = error?.response?.data?.detail
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      const parts = [
        typeof detail.message === 'string' ? detail.message : '',
        typeof detail.action === 'string' ? detail.action : '',
        ...(Array.isArray(detail.issues) ? detail.issues.slice(0, 3).map(String) : []),
      ].filter(Boolean)
      if (parts.length) error.message = parts.join(' ')
    }

    // Don't redirect on 401 for auth endpoints (login/register/forgot-password/reset-password)
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                          error.config?.url?.includes('/auth/register') ||
                          error.config?.url?.includes('/auth/forgot-password') ||
                          error.config?.url?.includes('/auth/reset-password')
    
    const originalRequest = error.config as (typeof error.config & { _sessionRefreshRetried?: boolean })
    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest?._sessionRefreshRetried && localStorage.getItem('refresh_token')) {
      originalRequest._sessionRefreshRetried = true
      try {
        const nextToken = await refreshAccessTokenIfNeeded(true)
        if (nextToken) {
          originalRequest.headers = originalRequest.headers || {}
          originalRequest.headers.Authorization = `Bearer ${nextToken}`
          return api(originalRequest)
        }
      } catch {
        // Renewal failed or the refresh token expired; fall through to the
        // normal single-shot session-expired handling below.
      }
    }

    if (error.response?.status === 401 && !isAuthEndpoint) {
      expireClientSession('invalid')
    }
    return Promise.reject(error)
  }
)

export default api
